/**
 * Utility functions to extract, normalize, and check user roles from PKCE/JWT/SSO tokens.
 * 
 * Token payload structure from SSO:
 * - decodedAccess.payload.roles: ['Manufacturer'] or ['Brand'] or ['Brand ']
 * - decodedAccess.payload.companyId: 69
 * - decodedAccess.payload.userId: 223
 * - decodedAccess.payload.users: { id: 223, username: 'ManufacturerSanthosh', ... }
 * 
 * Role IDs (if numeric):
 * - 108: Brand
 * - 106: Manufacturer
 */

export const ROLE_IDS = {
  BRAND: 108,
  MANUFACTURER: 106,
};

/**
 * Extracts all role strings and numbers from the user object.
 * @param {object|null} user
 * @returns {Array<string|number>}
 */
export function getRawRolesList(user) {
  if (!user) return [];

  const candidates = [
    user.roles,
    user.role,
    user.roleName,
    user.role_name,
    user.roleId,
    user.role_id,
    user.roleIds,
    user.userRole,
    user.designation,
    user.type,
    user.userType,
  ];

  const list = [];

  for (const item of candidates) {
    if (Array.isArray(item)) {
      for (const val of item) {
        if (typeof val === "string" && val.trim()) {
          list.push(val.trim());
        } else if (typeof val === "number" && !isNaN(val)) {
          list.push(val);
        } else if (typeof val === "object" && val !== null) {
          if (val.name) list.push(String(val.name).trim());
          if (val.title) list.push(String(val.title).trim());
          if (val.id) list.push(val.id);
          if (val.roleId) list.push(val.roleId);
        }
      }
    } else if (typeof item === "string" && item.trim()) {
      list.push(item.trim());
    } else if (typeof item === "number" && !isNaN(item)) {
      list.push(item);
    } else if (typeof item === "object" && item !== null) {
      if (item.name) list.push(String(item.name).trim());
      if (item.title) list.push(String(item.title).trim());
      if (item.id) list.push(item.id);
      if (item.roleId) list.push(item.roleId);
    }
  }

  return list;
}

/**
 * Extracts the user's role display name (e.g. 'Manufacturer' or 'Brand').
 * @param {object|null} user
 * @returns {string} The normalized role string.
 */
export function extractRoleFromUser(user) {
  if (!user) return "";

  const roles = getRawRolesList(user);

  for (const r of roles) {
    if (typeof r === "string") {
      const clean = r.trim().toLowerCase();
      if (clean === "brand" || clean.startsWith("brand") || clean.includes("brand")) {
        return "Brand";
      }
      if (clean === "manufacturer" || clean.startsWith("manufacturer") || clean.includes("manufacturer")) {
        return "Manufacturer";
      }
    }
    if (Number(r) === ROLE_IDS.BRAND) {
      return "Brand";
    }
    if (Number(r) === ROLE_IDS.MANUFACTURER) {
      return "Manufacturer";
    }
  }

  // If a string role is present that doesn't match predefined names, return it
  for (const r of roles) {
    if (typeof r === "string" && r.trim() && isNaN(Number(r))) {
      return r.trim();
    }
  }

  return "";
}

/**
 * Checks if a given role or user object corresponds to a Brand role.
 * Matches:
 * - roles: ['Brand'], ['Brand '], ['brand']
 * - roles: [108] or roleId: 108
 * @param {string|object|number|null} roleOrUser
 * @returns {boolean}
 */
export function isBrandRole(roleOrUser) {
  if (!roleOrUser) return false;

  if (typeof roleOrUser === "string") {
    const clean = roleOrUser.trim().toLowerCase();
    return clean === "brand" || clean.startsWith("brand") || clean.includes("brand");
  }

  if (typeof roleOrUser === "number") {
    return roleOrUser === ROLE_IDS.BRAND;
  }

  const roles = getRawRolesList(roleOrUser);
  for (const r of roles) {
    if (typeof r === "string") {
      const clean = r.trim().toLowerCase();
      if (clean === "brand" || clean.startsWith("brand") || clean.includes("brand")) {
        return true;
      }
    }
    if (Number(r) === ROLE_IDS.BRAND) {
      return true;
    }
  }

  return false;
}

/**
 * Checks if a given role or user object corresponds to a Manufacturer role.
 * Matches:
 * - roles: ['Manufacturer'], ['Manufacturer '], ['manufacturer']
 * - roles: [106] or roleId: 106
 * @param {string|object|number|null} roleOrUser
 * @returns {boolean}
 */
export function isManufacturerRole(roleOrUser) {
  if (!roleOrUser) return false;

  if (typeof roleOrUser === "string") {
    const clean = roleOrUser.trim().toLowerCase();
    return clean === "manufacturer" || clean.startsWith("manufacturer") || clean.includes("manufacturer");
  }

  if (typeof roleOrUser === "number") {
    return roleOrUser === ROLE_IDS.MANUFACTURER;
  }

  const roles = getRawRolesList(roleOrUser);
  for (const r of roles) {
    if (typeof r === "string") {
      const clean = r.trim().toLowerCase();
      if (clean === "manufacturer" || clean.startsWith("manufacturer") || clean.includes("manufacturer")) {
        return true;
      }
    }
    if (Number(r) === ROLE_IDS.MANUFACTURER) {
      return true;
    }
  }

  return false;
}

/**
 * Returns the correct destination route based on the extracted user role:
 * - Brand (role name 'Brand' or ID 108) -> '/brand'
 * - Manufacturer (role name 'Manufacturer' or ID 106) -> '/dashboard'
 * @param {object|string|number|null} user
 * @returns {string} '/brand' or '/dashboard'
 */
export function getDefaultDashboardPath(user) {
  if (isBrandRole(user)) {
    return "/brand";
  }
  return "/dashboard";
}

/**
 * Extracts companyId from user object or auth state.
 * @param {object|null} authOrUser
 * @returns {number|null}
 */
export function extractCompanyId(authOrUser) {
  if (!authOrUser) return null;
  let cid =
    authOrUser.companyId ??
    authOrUser.loginUser?.companyId ??
    authOrUser.loginUser?.company?.id ??
    authOrUser.company?.id ??
    null;
  if (Array.isArray(cid)) cid = cid[0];
  return cid !== null && cid !== undefined ? Number(cid) : null;
}

/**
 * Extracts userId from user object or auth state.
 * @param {object|null} authOrUser
 * @returns {number|null}
 */
export function extractUserId(authOrUser) {
  if (!authOrUser) return null;
  let uid =
    authOrUser.userId ??
    authOrUser.id ??
    authOrUser.loginUser?.userId ??
    authOrUser.loginUser?.id ??
    authOrUser.loginUser?.users?.id ??
    null;
  return uid !== null && uid !== undefined ? Number(uid) : null;
}

